import { useMemo } from 'react';
import { get, map, reduce } from 'lodash';
import { useSelector } from 'react-redux';

import { getResourceEntity } from 'js/lib/PipelineUtils';
import { getConnectionOrVendorType } from 'js/lib/ResourceUtils';
import {
  RESOURCE_TYPE_DATA_SET,
  RESOURCE_TYPE_SINK,
  RESOURCE_TYPE_SOURCE,
} from 'js/constants/resourceTypes';
import { FlatFlow } from 'new/entities/FlatFlow';
import { getStatusInfo } from 'new/hooks/adapters/statusInfo';
import { createSelector } from 'reselect';

const COLOR_SEVERITY = { OK: 0, WARNING: 1, ERROR: 2 };

const flatFlowsSelector = createSelector(
  [
    (state: any) => state.entities,
    (state: any) => state.pipelines,
    (state: any) => state.metrics.dashboard,
    (state: any) => state.paging.metaInfoById,
  ],
  (entities, pipelines, metrics, metaInfoById) => {
    return {
      entities,
      flowNodes: entities.flowNodes,
      vendors: entities.vendors,
      metaInfoById,
      metrics,
      flat: pipelines.flat,
      branches: pipelines.branches,
    };
  },
);

export function useFlatFlows(): FlatFlow[] {
  const { flat, entities, flowNodes, vendors, metrics, metaInfoById, branches } =
    useSelector(flatFlowsSelector);

  return useMemo(() => {
    const mappedFlows = flat.map((flow) => {
      const { root, leaf, dataSets: sets } = flow;
      const flowName = flowNodes[root]?.name;
      const rootItem =
        root && !!flowNodes[root] ? getResourceEntity(flowNodes[root], entities) : null;
      const leafItem =
        leaf && !!flowNodes[leaf] ? getResourceEntity(flowNodes[leaf], entities) : null;

      const dataSets = map(sets, (dataSet) => getResourceEntity(flowNodes[dataSet], entities));

      // source/sink icons
      const isLeafSink = leafItem?.type === 'DATA_SINK';
      const isRootDataset = rootItem?.type === 'DATA_SET';
      let rootConnectionInfo = null,
        leafConnectionInfo = null;

      if (!isRootDataset && metaInfoById) {
        //root is source. get connection type.
        const rootConnectionType = getConnectionOrVendorType(
          rootItem,
          vendors,
          'source_type',
          'vendor_id',
        );
        rootConnectionInfo = metaInfoById[rootConnectionType] || null;
      }

      if (isLeafSink && metaInfoById) {
        const leafConnectionType = getConnectionOrVendorType(
          leafItem,
          vendors,
          'sink_type',
          'vendor_id',
        );
        leafConnectionInfo = metaInfoById[leafConnectionType] || null;
      }

      let rootConnectionIcon;
      let rootConnectionName;
      let leafConnectionIcon;
      let leafConnectionName;

      if (rootConnectionInfo) {
        rootConnectionIcon = rootConnectionInfo.iconSm;
        rootConnectionName = rootConnectionInfo.name;
      }

      if (leafConnectionInfo) {
        leafConnectionIcon = leafConnectionInfo.iconSm;
        leafConnectionName = leafConnectionInfo.name;
      }

      // metrics
      let sourceDataSetId;

      if (leafItem) {
        const branchItems = branches[leafItem.nodeKey]?.items || [];

        if (branchItems.length > 1) {
          const dataSetItem = flowNodes[branchItems[1]];

          if (dataSetItem.type === 'DATA_SET') {
            sourceDataSetId = dataSetItem.data_set_id;
          }
        }
      }

      let leafResourceType;

      switch (leafItem && leafItem.type) {
        case 'SOURCE':
          leafResourceType = RESOURCE_TYPE_SOURCE;
          break;

        case 'DATA_SET':
          leafResourceType = RESOURCE_TYPE_DATA_SET;
          break;

        case 'DATA_SINK':
          leafResourceType = RESOURCE_TYPE_SINK;
          break;

        default:
      }

      // row color
      let rowColorSeverity =
        COLOR_SEVERITY[get(metrics, `${RESOURCE_TYPE_SOURCE}.${rootItem.id}.status`, 'OK')];

      if (leafResourceType) {
        rowColorSeverity = Math.max(
          rowColorSeverity,
          COLOR_SEVERITY[get(metrics, `${leafResourceType}.${leafItem.id}.status`, 'OK')],
        );
      }
      rowColorSeverity = reduce(
        dataSets,
        (severity, dataSet) =>
          Math.max(
            severity,
            COLOR_SEVERITY[get(metrics, `${RESOURCE_TYPE_DATA_SET}.${dataSet.id}.status`, 'OK')],
          ),
        rowColorSeverity,
      );

      //Use Nexset in case source has multiple Nexsets
      const sourceRecordCount = get(
        metrics,
        `${RESOURCE_TYPE_DATA_SET}.${sourceDataSetId}.records`,
        0,
      );
      //Use source because ingestion errors are applicable to all possible source Nexsets
      const sourceErrorCount = get(metrics, `${RESOURCE_TYPE_SOURCE}.${rootItem.id}.errors`, 0);
      const sinkRecordCount =
        (isLeafSink && get(metrics, `${RESOURCE_TYPE_SINK}.${leafItem.id}.records`, 0)) || 0;
      const sinkErrorCount =
        (isLeafSink && get(metrics, `${RESOURCE_TYPE_SINK}.${leafItem.id}.errors`, 0)) || 0;
      const sourceRecordVolume = get(
        metrics,
        `${RESOURCE_TYPE_DATA_SET}.${sourceDataSetId}.size`,
        0,
      );
      const sinkRecordVolume =
        (isLeafSink && get(metrics, `${RESOURCE_TYPE_SINK}.${leafItem.id}.size`, 0)) || 0;

      return {
        root: rootItem && {
          ...rootItem,
          name: flowName || rootItem.name || rootItem.flow_node_id || rootConnectionName,
          icon: rootConnectionIcon,
          isNexset: isRootDataset,
          statusInfo: getStatusInfo(rootItem.status),
        },
        leaf: leafItem && {
          ...leafItem,
          name: leafItem.name || leafItem.id || leafConnectionName,
          icon: leafConnectionIcon,
          isSink: isLeafSink,
        },
        isLeafSink,
        rootConnectionName,
        rootConnectionIcon,
        leafConnectionName,
        leafConnectionIcon,
        severity: rowColorSeverity,
        sourceRecordCount,
        sinkRecordCount,
        sourceErrorCount,
        sinkErrorCount,
        sourceRecordVolume,
        sinkRecordVolume,
        nexsets: map(dataSets, (dataSet) => getResourceEntity(flowNodes[dataSet], entities)),
      };
    });
    return mappedFlows;
  }, [entities, flat, branches, vendors, metrics, metaInfoById]);
}

export function useFlatFlowsById(): Record<number, FlatFlow> {
  const flows = useFlatFlows();
  return useMemo(
    () =>
      flows.reduce((acc, flow) => {
        acc[flow.root.origin_node_id] = flow;
        return acc;
      }, {}),
    [flows],
  );
}
